import type { AxiosResponse } from 'axios';
import axios from 'axios';
import {
  mapPinnedPageDtoToModel,
  mapPinnedPagesDtoToModel,
  mapPinnedPageModelToDto,
  mapSavablePinnedPageModelToDto,
  mapPinnedPageTrackRequestModelToDto,
} from './mapper';

// ATTENTION: Keep in mind to test for CORS error if you remove the trailing slash `/`
const buildEndpoint = ({ siteId, pinId }: { siteId: SiteId | undefined; pinId?: number }) =>
  `${['/api/heatmaps/v1/sites', siteId, 'saved_heatmaps', pinId].filter(Boolean).join('/')}/`;

const getData = <T>(response: AxiosResponse<T>): T => response.data;

export async function getPinnedPages(data: HeatmapAPI.WithSiteId): Promise<HeatmapAPI.PinnedPages> {
  const { siteId } = data;

  const response = await axios.get(buildEndpoint({ siteId })).then(getData);

  return mapPinnedPagesDtoToModel(response);
}

export async function addPinnedPage(
  data: HeatmapAPI.WithSiteId & HeatmapAPI.SavablePinnedPage,
): Promise<HeatmapAPI.PinnedPage> {
  const { siteId, ...pinnedPageData } = data;
  const response = await axios
    .post(buildEndpoint({ siteId }), mapSavablePinnedPageModelToDto(pinnedPageData))
    .then(getData);

  return mapPinnedPageDtoToModel(response);
}

export async function removePinnedPage(
  data: HeatmapAPI.WithSiteId & HeatmapAPI.PinnedPage,
): Promise<AxiosResponse> {
  const { siteId, id: pinId } = data;
  return axios.delete(buildEndpoint({ siteId, pinId }));
}

export async function updatePinnedPage(
  data: HeatmapAPI.WithSiteId & HeatmapAPI.PinnedPage,
): Promise<HeatmapAPI.PinnedPage> {
  const { siteId, ...pinnedPageData } = data;
  const { id: pinId } = pinnedPageData;

  // eslint-disable-next-line camelcase, @typescript-eslint/no-unused-vars
  const { created_by, id, ...payload } = mapPinnedPageModelToDto(pinnedPageData);

  const response = await axios.post(buildEndpoint({ siteId, pinId }), payload).then(getData);

  return mapPinnedPageDtoToModel(response);
}

export async function searchPinnedPage(
  data: HeatmapAPI.WithSiteId & HeatmapAPI.UnsavedPinnedPage,
): Promise<HeatmapAPI.PinnedPages> {
  const { siteId, ...pinnedPageData } = data;
  const response = await axios
    .get(buildEndpoint({ siteId }), {
      params: {
        match_operation: pinnedPageData.matchOperation,
        pattern: encodeURIComponent(pinnedPageData.pattern),
      },
    })
    .then(getData);

  return mapPinnedPagesDtoToModel(response);
}

export async function trackPinnedPage(
  data: HeatmapAPI.PinnedPageTrackRequest & HeatmapAPI.WithSiteId,
): Promise<HeatmapAPI.Response> {
  const { siteId, eventType } = data;
  return axios
    .get(`/api/v1/sites/${siteId}/pinned_pages/${eventType}/track`, {
      params: mapPinnedPageTrackRequestModelToDto(data),
    })
    .then(getData);
}
